import DocumentService from '../../api/documents';
import ScanService from '../../api/scans';
import Vue from 'vue';
import _ from 'lodash';
import payloadHelper from 'src/services/payloadHelper';

const state = {
  filterAggregations: {},
  currentFiltering: {},
  filtersLoading: {
    publication: false,
    patent: false,
    funding: false,
    fundingPublication: false,
  },
  filterHistory: {
    publication: [],
    patent: [],
    funding: [],
    fundingPublication: [],
  },
  filterHistoryLoading: {
    publication: true,
    patent: true,
    funding: true,
    fundingPublication: true,
  },
  activeFilterSetId: {
    publication: null,
    patent: null,
    funding: null,
    fundingPublication: null,
  },
  awaitingFilterHistoryDocCount: {
    publication: false,
    patent: false,
    funding: false,
    fundingPublication: false,
  },
  filterAggregationsError: {
    publication: false,
    patent: false,
    funding: false,
    fundingPublication: false,
  },
  filterPaneOpen: true,
};

const getters = {
  filterAggregations: (state) => {
    return state.filterAggregations;
  },
  currentFiltering: (state) => {
    return state.currentFiltering;
  },
  filtersLoading: (state) => {
    return state.filtersLoading;
  },
  filterHistory: (state) => {
    return state.filterHistory;
  },
  filterHistoryLoading: (state) => {
    return state.filterHistoryLoading;
  },
  activeFilterSetId: (state) => {
    return state.activeFilterSetId;
  },
  awaitingFilterHistoryDocCount: (state) => {
    return state.awaitingFilterHistoryDocCount;
  },
  filterAggregationsError: (state) => {
    return state.filterAggregationsError;
  },
  filterPaneOpen: (state) => {
    return state.filterPaneOpen;
  },
};

const actions = {
  async fetchFilters({ commit, dispatch }, payload) {
    // Fetch filter aggregation data from Document Service
    commit('setFiltersLoading', { source: payload.source, data: true });
    commit('setFilterAggregationsError', {
      source: payload.source,
      data: false,
    });

    // Clone payload to manipulate here
    let request = _.cloneDeep(payload);
    // Set the size to 0 because the documents are not needed in this request
    request.size = 0;

    const documentService = new DocumentService();
    let response = await documentService.fetchFilters(request);

    if (response && !response.error) {
      // Used to sort filter aggregations in the order they should display in the UI -- The order they appear here will be the order they appear in the filters list
      const filterOrder = {
        publication: [
          'threshold',
          'venue_name',
          'concepts.display_name',
          'affiliation_types',
          'citation_count',
          'publication_year',
          'type',
          'advisories.advisory_type',
          'author_affiliation_country_names',
          'author_affiliation_state_names',
          'author_affiliation_names',
          'author_names',
          'funder_names',
        ],
        patent: [
          'threshold',
          'filing_year',
          'priority_year',
          'granted_year',
          'citation_count',
          'kind_code_name',
          'status',
          'authority_name',
          'inventor_names',
          'inventor_country_names',
          'inventor_state_names',
          'assignee_names',
          'assignee_country_names',
          'assignee_state_names',
          'cpc_codes',
        ],
        funding: [
          'threshold',
          'source.db',
          'funding_us_fiscal_year',
          'funding_amount',
          'category',
          'type',
          'psc_codes',
          'recipient_names',
          'recipient_types',
          'recipient_countries',
          'recipient_states',
          'funder_names',
          'funder_types',
          'funder_countries',
          'funder_states',
        ],
        fundingPublication: [
          'threshold',
          'publication_year',
          'advisories.advisory_type',
          'author_affiliation_names',
          'affiliation_types',
          'author_affiliation_country_names',
          'author_affiliation_state_names',
          'funder_names',
          'funder_types',
          'funder_countries',
          'funder_states',
        ],
      };

      // Loop through filterOrder to re-order filters for display in the UI
      const sortedFilters = {};

      filterOrder[payload.source].forEach((key) => {
        if (response.hasOwnProperty(key)) {
          sortedFilters[key] = response[key];
        }
      });

      // Build payload to pass to mutation -- Pass in the sortedFilters
      const _payload = {
        source: payload.source,
        data: sortedFilters,
      };

      commit('setFilterAggregations', _payload);

      if (!payload.createScanVersion) {
        // If we are not creating a new scan version, clear history load state as we aren't waiting for a new filter history to return from the scan service.
        dispatch('setFilterHistoryLoading', {
          source: payload.source,
          data: false,
        });
      }
    } else {
      // If there is an error...

      // Set filter error state to true
      commit('setFilterAggregationsError', {
        source: payload.source,
        data: true,
      });
      // Set filter history loading state to false as we are not waiting for a new filter history to return from the scan service.
      dispatch('setFilterHistoryLoading', {
        source: payload.source,
        data: false,
      });
    }
    commit('setFiltersLoading', { source: payload.source, data: false });
  },
  async updateGlobalFilters({ commit, dispatch, rootGetters }, payload) {
    // Payload includes up to five properties: a filtering object, and optionally a source, activeFilterSetId, createScanVersion, and getFilterHistoryDocCount
    // If source passed in payload, use that, otherwise use the current activeDataSource from the application module
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];

    // Set filter history loading state to true while we await new filter history data. Will be set false when filter history is set.
    commit('setFilterHistoryLoading', { source, data: true });

    // Commit filtering for current active data source
    if (payload && payload.filters) {
      const currentFilteringPayload = {
        data: payload.filters,
        source: source,
      };
      commit('setCurrentFiltering', currentFilteringPayload);
    }

    if (!source.toLowerCase().includes('funding')) {
      // Publication and patent tab filters
      // Build payload to pass to fetchDocuments action for publication and patent sources
      const _documentsPayload = {
        createScanVersion: payload.hasOwnProperty('createScanVersion')
          ? payload.createScanVersion
          : true, // If createScanVersion is passed in payload, use that, else default to true. We want to create a new scan version when the scan owner updates filters. We don't want to create a new scan version at scan load when loading last-applied filters, or if a non-owner updates filters.
        activeFilterSetId: payload.activeFilterSetId || null, // The payload contains activeFilterSetId if the user loaded filters from history. This is passed to the scan service when creating a new version so the service knows we don't need to create a new filter set.
        getFilterHistoryDocCount: payload.getFilterHistoryDocCount || false, // The payload contains getFilterHistoryDocCount if there is no filter history for this scan, ie it was created before filter history implementation. We need to get the doc_count after documents load and send to scan service.
        skip_docs: payload.skip_docs || false, // The payload contains skip_docs if the scan was just created - the documents were already fetched in the scan service and we don't need to fetch them again.
        source,
      };

      // Dispatch action in documents module to fetch documents based on new filters
      dispatch('documents/fetchDocuments', _documentsPayload, { root: true });

      // Dispatch action in leaders module to fetch leaders based on new filters
      dispatch('leaders/fetchLeaders', { source }, { root: true });

      // If this isn't the initial load, dispatch action to setShouldRecomputeTrends to true as trends need to be recomputed when filters change. Uses initialLoad instead of createScanVersion because we also want recompute for shared users.
      if (!payload.initialLoad) {
        dispatch(
          'trends/shouldRecomputeTrends',
          { source, data: true },
          { root: true }
        );
      }
    } else {
      // Funding tab filters - no need to fetch documents
      // Initial scan load - get the document counts for the funding tabs
      if (payload.initialLoad) {
        // Get the counts w/filters for the source
        await dispatch(
          'funding/getFundingCounts',
          { source, type: 'filteredFundingCounts' },
          { root: true }
        );

        if (payload.getFilterHistoryDocCount) {
          // If there is no filter history for this scan, ie it was created before funding filter history implementation, we need to get the doc_count after documents load and send to scan service.
          dispatch('updateFilterSetDocCount', { source });
        }
      } else {
        // This is not the initial scan load, either this was called from the funding tab on load or when filters have been updated. We don't call this at scan load to avoid running all of the aggs for a scan at once in case the user never visits the funding tab.
        const {
          query_builder,
          filters,
          scan_type,
          scan_id,
          version_id,
          user_id,
          name,
          description,
          query_elements,
          query,
        } = payloadHelper.buildBasePayloadProps(rootGetters, source);

        // Build payload to pass to fetchFilters action for funding tab sources
        const _fundingFiltersPayload = {
          query: query_builder,
          scan_id: version_id,
          scan_type,
          filters,
          source,
          skip_aggs: false,
          createScanVersion: payload.createScanVersion ? true : false,
        };
        // Dispatch action to fetch filters for funding tab source
        dispatch('fetchFilters', _fundingFiltersPayload);

        // Dispatch actions in funding module to update data for the funding tabs if this isn't the initial load. For the initial load we simply want to fetch filters and update filter history.

        // Dispatch action in funding module to fetch table and graph data based on new filters
        dispatch('funding/getTableData', { source }, { root: true });
        // Dispatch action in funding module to fetch graph data based on new filters
        dispatch('funding/getGraphData', { source }, { root: true });

        // Build payload to pass to Scan service to create a new scan version
        if (payload.createScanVersion) {
          // Get the updated filtered counts for the funding tab source
          const filteredFundingCounts = await dispatch(
            'funding/getFundingCounts',
            { source, type: 'filteredFundingCounts' },
            { root: true }
          );
          const _versionPayload = {
            scan_type,
            scan_id,
            current_version_id: version_id,
            user_id,
            name,
            query,
            query_elements,
            description,
            // Below properties are used to update filter history when a new scan version is created
            source,
            filters: filters ? filters : {},
            active_filter_set_id: payload.activeFilterSetId || null,
            filter_set_doc_count: filteredFundingCounts.docCount,
          };
          // Dispatch createScanVersion action in Scans module
          dispatch('scans/createScanVersion', _versionPayload, { root: true });
        }
      }
    }
  },
  setFilterHistory({ commit }, payload) {
    // Destructure source and data from payload
    const { source, data } = payload;
    // Update filter history in state
    commit('setFilterHistory', { source, data });
    // Update active filter set id in state
    commit('setActiveFilterSetId', {
      source,
      data: payload.activeFilterSetId || data[0].filter_set_id, //If activeFilterSetId is passed in payload, use that, otherwise use the first filter set in the array
    });
    // Update awaiting doc count in state
    commit('setAwaitingFilterHistoryDocCount', {
      source,
      data: payload.awaitingFilterHistoryDocCount ? true : false,
    });
    // Set filter history loading state to false now that we've set filter history state
    commit('setFilterHistoryLoading', { source: payload.source, data: false });
  },
  async pinUnpinRenameFilterSet({ commit }, payload) {
    const _payload = {
      filter_set_id: payload.filter_set_id,
      pinned: payload.pinned,
      name: payload.name,
    };
    const scanService = new ScanService();
    const response = await scanService.pinUnpinRenameFilterSet(_payload);
    if (response && response.data) {
      commit('updateFilterSet', { source: payload.source, data: payload });
    }
  },
  setActiveFilterSetId({ commit }, payload) {
    const { source, data } = payload;
    commit('setActiveFilterSetId', {
      source,
      data,
    });
  },
  setFilterHistoryLoading({ commit }, payload) {
    const { source, data } = payload;
    commit('setFilterHistoryLoading', { source, data });
  },
  async updateFilterSetDocCount({ commit, state, rootGetters }, payload) {
    // Destructure source from payload
    const { source } = payload;
    // Get the activeFilterSetId from state
    const activeFilterSetId = state.activeFilterSetId[source];
    let docCount = 0;

    if (source === 'publication' || source === 'patent') {
      // Get the doc count from the documents module
      docCount = rootGetters['documents/getPagination'][source].rowsNumber;
    } else {
      docCount = rootGetters['funding/filteredFundingCounts'][source].docCount;
    }
    // Build payload to pass to Scan service
    const _payload = {
      filter_set_id: activeFilterSetId,
      doc_count: docCount,
    };
    // Make request to Scan service to update the doc count for the active filter set
    const scanService = new ScanService();
    const response = await scanService.updateFilterSetDocCount(_payload);
    if (response && response.data) {
      // Update the filter set in state
      commit('updateFilterSet', { source, data: response.data[0] });
      // Commit mutation to set awaiting doc count false now that we have updated the doc count
      commit('setAwaitingFilterHistoryDocCount', { source, data: false });
    }
  },
  clearData({ commit }) {
    // Reset store state to initial values
    commit('setInitialState');
  },
  setFilterPaneOpen({ commit }, payload) {
    commit('setFilterPaneOpen', payload);
  },
};

const mutations = {
  setFiltersLoading(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.filtersLoading, payload.source, payload.data);
  },
  setFilterAggregations(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.filterAggregations, payload.source, payload.data);
  },
  setCurrentFiltering(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.currentFiltering, payload.source, payload.data);
  },
  setFilterHistory(state, payload) {
    Vue.set(state.filterHistory, payload.source, payload.data);
  },
  updateFilterSet(state, payload) {
    // Find index of filter set in filter history array
    const filterSetIndex = state.filterHistory[payload.source].findIndex(
      (filterSet) => filterSet.filter_set_id === payload.data.filter_set_id
    );

    if (filterSetIndex > -1) {
      // Update filter set in filter history array
      Object.assign(
        state.filterHistory[payload.source][filterSetIndex],
        payload.data
      );
    }
  },
  setActiveFilterSetId(state, payload) {
    Vue.set(state.activeFilterSetId, payload.source, payload.data);
  },
  setFilterHistoryLoading(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.filterHistoryLoading, payload.source, payload.data);
  },
  setAwaitingFilterHistoryDocCount(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.awaitingFilterHistoryDocCount, payload.source, payload.data);
  },
  setInitialState(state) {
    state.filtersLoading = {
      publication: false,
      patent: false,
    };
    state.filterAggregations = {};
    state.currentFiltering = {};
    state.filterHistory = {
      publication: [],
      patent: [],
    };
    state.filterHistoryLoading = {
      publication: true,
      patent: true,
    };
    state.activeFilterSetId = {
      publication: null,
      patent: null,
    };
  },
  setFilterAggregationsError(state, payload) {
    Vue.set(state.filterAggregationsError, payload.source, payload.data);
  },
  setFilterPaneOpen(state, payload) {
    state.filterPaneOpen = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
